import { CustomType, ProgressType, UserDailyDiaryProgress, UserDailyProgress } from '../../services/api.types'
import { t } from 'ttag'
import dayjs from '../../dayjsConfig'
import { formatThousands } from '../../common/helpers/translations'
import * as React from 'react'
import { Fragment, useState } from 'react'
import './YumCustomTable.scss'
import { getLongDateString } from '../../common/helpers/date'
import { convertMinutesToHoursAndMinutes } from '../../views/Progress/Progress'
import { List } from '@material-ui/core'
import { useStyles } from '../../views/ChallengeInfo/ChallengeInfo'
import { getListItems } from '../../views/ChallengeInfo/ChallengeInfo.legacy'
import { unitsDisplayDurationCalorieLong, unitsDisplayDurationMinuteLong } from '../../common/helpers/units'
import { YumActivityIcon } from '../YumActivityIcon/YumActivityIcon'
import { CustomTypeCategory } from '../../services/CustomTypes'

import { ReactComponent as DoneIcon } from '../../assets/v3/icons/done.svg'
import { ReactComponent as NotDoneIcon } from '../../assets/v3/icons/not-done.svg'
import { primaryBlue } from '../../styles/material-ui-theme-v2'
import { ReactComponent as ExpandIcon } from '../../assets/v3/icons/expand.svg'

export interface IStateProps {
  data: UserDailyDiaryProgress[]
  valueGoal: number | null
  units: string
  manualEntryEnabled: boolean
  showDigits?: boolean
  suffix?: string
  showTotalSum?: boolean
  showSeparateValues?: boolean
  isBinary?: boolean
  dailyMax?: number | null
  activityIndexDayDetails?: { [date: string]: UserDailyProgress }
}

export default function YumLogTable(props: IStateProps) {
  const {
    data,
    valueGoal,
    units,
    manualEntryEnabled,
    showDigits,
    suffix,
    showTotalSum,
    showSeparateValues: showSeparateValuesRaw,
    isBinary,
    dailyMax,
    activityIndexDayDetails,
  } = props

  const classes = useStyles()
  const [openDays, setOpenDays] = useState([] as string[])

  const expandable = !!activityIndexDayDetails

  const toggleDetails = (date: string) => {
    setOpenDays((prev) => (prev.includes(date) ? prev.filter((d) => d !== date) : [...prev, date]))
  }

  const getActivityIndexListItems = (date: string) => {
    const day = activityIndexDayDetails?.[date]
    if (!day) return []
    return [
      {
        label: t`Steps`,
        total: day.stepsTotal,
        goal: day.stepsGoal,
        percentage: day.stepsPercentage,
        category: CustomTypeCategory.STEPS,
      },
      {
        label: unitsDisplayDurationCalorieLong,
        total: day.caloriesTotal,
        goal: day.caloriesGoal,
        percentage: day.caloriesPercentage,
        category: CustomTypeCategory.CALORIES,
      },
      {
        label: unitsDisplayDurationMinuteLong,
        total: day.intensiveMinutesTotal,
        goal: day.intensiveMinutesGoal,
        percentage: day.intensiveMinutesPercentage,
        category: CustomTypeCategory.INTENSIVE_MINUTES,
      },
    ].map(({ label, total, goal, percentage, category }) => ({
      label,
      content: `${formatThousands(total)} / ${formatThousands(goal)} = ${percentage}%`,
      Icon: <YumActivityIcon category={category} color={'darkBlue'} isInactive={true} extraPadding={true} />,
    }))
  }

  const valueBEM = (met: boolean) => {
    const base = 'data-item__value'
    const classArray = [base]
    if (met) {
      classArray.push(`${base}--met`)
    }
    return classArray.join(' ')
  }

  const anyIntegrationValues = data && !!data.find(({ integrationValue }) => integrationValue > 0)
  const anyManualValues = data && !!data.find(({ manualValue }) => manualValue > 0)
  const showSeparateValues = manualEntryEnabled && (anyIntegrationValues || showSeparateValuesRaw) && anyManualValues

  const today = new Date()
  const relevantData = data ? data.filter(({ date }) => dayjs(date).isBefore(today)) : []
  let totalSum = showTotalSum && data ? relevantData.reduce((a, { sum }) => a + sum, 0) : null
  if (showTotalSum && totalSum && suffix === '%') {
    totalSum = Math.floor(totalSum / relevantData.length)
  }

  const formatValue = (value: number) => {
    return (
      <>
        {units === t`Sleep`
          ? value > 0
            ? convertMinutesToHoursAndMinutes(value)
            : '-'
          : formatThousands(value, showDigits ? 2 : 0)}
        {suffix}
      </>
    )
  }

  const totalColumns = 1 + (showSeparateValues ? 1 : 0) + 1

  return (
    <table className={'custom-table custom-table__me'}>
      <thead>
        <tr>
          <th scope="col">
            <svg
              style={{ width: '1rem', height: '1rem' }}
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M8 16C12.4183 16 16 12.4183 16 8C16 3.58172 12.4183 0 8 0C3.58172 0 0 3.58172 0 8C0 12.4183 3.58172 16 8 16ZM6.4 4.8C6.4 3.91875 7.11563 3.2 8 3.2C8.88438 3.2 9.6 3.91875 9.6 4.8V6.4H11.2C12.0844 6.4 12.8 7.11875 12.8 8C12.8 8.88125 12.0844 9.6 11.2 9.6H8C7.11563 9.6 6.4 8.88125 6.4 8V4.8Z"
                fill={primaryBlue}
              />
            </svg>
            {t`Date`}
          </th>
          {showSeparateValues && (
            <th scope="col" className={'data-item__value data-item__value--center'}>
              {t`Integration`}
              <br />
              <span className={'opacity-50'}>{t`Manual entry`}</span>
            </th>
          )}
          <th scope="col" className={'data-item__value'}>
            {isBinary ? t`Done` : units}
          </th>
        </tr>
      </thead>
      <tbody>
        {data &&
          data.map(({ date, integrationValue, manualValue, sum }, keyIndex) => {
            if (!dayjs(date).isBefore(today)) return null
            const isOpen = openDays.includes(date)
            const allowOpening = expandable && sum > 0
            return (
              <Fragment key={keyIndex}>
                <tr
                  style={allowOpening ? { cursor: 'pointer', userSelect: 'none' } : {}}
                  onClick={() => allowOpening && toggleDetails(date)}
                >
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      {expandable && (
                        <div style={{ width: 40 }}>
                          <ExpandIcon
                            style={{
                              transition: 'transform 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
                              transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                              opacity: allowOpening ? 1 : 0.5,
                            }}
                          />
                        </div>
                      )}
                      <div>
                        <div className={'data-item__day'}>{dayjs(date).format('dddd')}</div>
                        <div className={'data-item__date'}>{getLongDateString(dayjs(date).toDate())}</div>
                      </div>
                    </div>
                  </td>
                  {showSeparateValues && (
                    <td className={'data-item__value data-item__value--center'}>
                      {formatValue(integrationValue)}
                      <br />
                      <span className={'opacity-50'}>{formatValue(manualValue)}</span>
                    </td>
                  )}
                  {manualEntryEnabled && (
                    <td className={valueBEM(valueGoal != null && sum >= valueGoal)}>
                      {!isBinary ? (
                        formatValue(sum)
                      ) : (
                        <>{valueGoal != null && sum >= valueGoal ? <DoneIcon /> : <NotDoneIcon />}</>
                      )}
                      {dailyMax && sum > dailyMax ? (
                        <div>
                          <small>
                            {t`Daily limit per user`}: {formatThousands(dailyMax)}
                          </small>
                        </div>
                      ) : null}
                    </td>
                  )}
                  {!manualEntryEnabled && (
                    <td className={valueBEM(valueGoal != null && integrationValue >= valueGoal)}>
                      {formatValue(integrationValue)}
                      {dailyMax && integrationValue > dailyMax ? (
                        <div>
                          <small>
                            {t`Daily limit per user`}: {formatThousands(dailyMax)}
                          </small>
                        </div>
                      ) : null}
                    </td>
                  )}
                </tr>

                {expandable && isOpen && (
                  <tr>
                    <td colSpan={totalColumns}>
                      <List component="div" role="presentation" style={{ marginLeft: '40px' }}>
                        {getActivityIndexListItems(date).map(getListItems(classes, true))}
                      </List>
                    </td>
                  </tr>
                )}
              </Fragment>
            )
          })}
      </tbody>
      {totalSum ? (
        <tfoot className="opacity-50">
          <tr>
            <td></td>
            {showSeparateValues && <td></td>}
            <td className={valueBEM(false)}>{formatValue(totalSum)}</td>
          </tr>
        </tfoot>
      ) : null}
    </table>
  )
}
